import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import MoveFundsButton from '@/components/MoveFundsButton';
import TransactionTable from '@/components/transactions/TransactionTable';
import { ROUTES } from '@/constants/routes';
import { useGetBankAccountDetails } from '@/hooks/requests/useBankAccounts';
import { useGetTransactionList } from '@/hooks/requests/useTransactions';
import { TransactionStatusCategory } from '@/types/transactions';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { Card, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export default function TransactionsListPage() {
  const { uuid = '' } = useParams();
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(uuid);
  const [processedPage, setProcessedPage] = useState(0);
  const [pendingPage, setPendingPage] = useState(0);

  const {
    transactionList: processedTransctionList,
    isTransactionListPending: isProcessedTransactionListPending,
    totalRecordCount: totalProcessedTransactions,
  } = useGetTransactionList(uuid, {
    page: processedPage,
    status: TransactionStatusCategory.PROCESSED,
  });
  const {
    transactionList: pendingTransctionList,
    totalRecordCount: totalPendingTransactions,
  } = useGetTransactionList(uuid, {
    page: pendingPage,
    status: TransactionStatusCategory.PENDING,
  });

  if (accountsDetailsIsPending) {
    return (
      <DefaultLayout header="Transactions">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!accountDetails) {
    return (
      <DefaultLayout header="Transactions">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account transactions. Our
            team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  return (
    <DefaultLayout
      header="Transactions"
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        { label: bankAccountName, href: `${ROUTES.BANK_ACCOUNTS}/${uuid}` },
        { label: 'Transactions', isActive: true },
      ]}
      buttons={[
        <MoveFundsButton
          key="move-funds"
          isFullWidth
          customerId={accountDetails.primaryOwner.id}
          bankAccountId={accountDetails.uuid}
        />,
      ]}>
      <Stack spacing={2}>
        {pendingTransctionList.length ? (
          <Card>
            <Typography variant="h5">Pending Transactions</Typography>
            <TransactionTable
              transactionList={pendingTransctionList}
              bankAccountId={uuid}
              hasPagination
              page={pendingPage}
              setPage={setPendingPage}
              totalRecordCount={totalPendingTransactions}
            />
          </Card>
        ) : null}
        <Card>
          <Typography variant="h5">Recent Transactions</Typography>
          {isProcessedTransactionListPending ? (
            <SVLoader />
          ) : (
            <TransactionTable
              transactionList={processedTransctionList}
              bankAccountId={uuid}
              hasPagination
              page={processedPage}
              setPage={setProcessedPage}
              totalRecordCount={totalProcessedTransactions}
            />
          )}
        </Card>
      </Stack>
    </DefaultLayout>
  );
}
